import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import {
  buildListingTitle,
  buildListingDescription,
  buildSlug,
  buildVehicleJsonLd,
  buildBreadcrumbJsonLd,
  SITE_URL,
} from '../utils/seo';
import {
  Typography,
  Space,
  Tag,
  Button,
  Divider,
  message,
} from 'antd';
import {
  CarOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  HeartOutlined,
  HeartFilled,
  ShareAltOutlined,
  FlagOutlined,
  PhoneOutlined,

  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { MdSpeed, MdCalendarToday, MdLocalGasStation, MdSettings, MdLocationOn, MdCreditCard, MdWarning, MdEco } from 'react-icons/md';
import { useAuth } from '../hooks/useAuth';
import { useListingDetails } from './hooks';
import MainHeader from '../header/MainHeader';
import Footer from '../footer/Footer';
import makesData from '../json/makes.json';
import ImageGallery from './components/ImageGallery';
import ReportModal from './components/ReportModal';
import SpecsGrid from './components/SpecsGrid';
import ImportedVehicleSection from './components/ImportedVehicleSection';
import FeatureList from './components/FeatureList';
import SimilarVehicles from './components/SimilarVehicles';
import StickyHeader from './components/StickyHeader';
import ListingDetailSkeleton from './components/ListingDetailSkeleton';
import './ListingDetails.css';

const { Text, Paragraph } = Typography;

const getStatusConfig = (status: string, t: (k: string) => string) => {
  const map: Record<string, { color: string; text: string; icon?: React.ReactNode }> = {
    DRAFT: { color: 'default', text: t('listing_details.status_draft') },
    TOKEN_VERIFICATION_PENDING: { color: 'orange', text: t('listing_details.status_token_review'), icon: <ClockCircleOutlined /> },
    TOKEN_VERIFIED: { color: 'blue', text: t('listing_details.status_token_verified'), icon: <CheckCircleOutlined /> },
    UNDER_APPROVAL: { color: 'purple', text: t('listing_details.status_under_approval'), icon: <ClockCircleOutlined /> },
    LIVE: { color: 'green', text: t('listing_details.status_active'), icon: <CheckCircleOutlined /> },
    ACTIVE: { color: 'green', text: t('listing_details.status_active'), icon: <CheckCircleOutlined /> },
    REJECTED: { color: 'red', text: t('listing_details.status_rejected'), icon: <ExclamationCircleOutlined /> },
    SUSPENDED: { color: 'orange', text: t('listing_details.status_suspended'), icon: <ExclamationCircleOutlined /> },
    SOLD: { color: 'default', text: t('listing_details.status_sold'), icon: <CheckCircleOutlined /> },
  };
  return map[status] || { color: 'default', text: status };
};

const getBrandLogo = (make?: string): string | null => {
  if (!make) return null;
  const localBrands = ['audi', 'bmw', 'chevrolet', 'ford', 'honda', 'hyundai', 'kia', 'lexus', 'mercedes', 'nissan', 'toyota', 'volkswagen'];
  const normalized = make.toLowerCase().trim();
  let localFilename = normalized.replace(/\s+/g, '-');
  if (localFilename.includes('mercedes')) localFilename = 'mercedes';
  if (localBrands.includes(localFilename)) return `/images/brands/${localFilename}.png`;
  const makeEntry = (makesData as any[]).find(
    (m) => m.makeName.toLowerCase() === normalized || normalized.includes(m.makeName.toLowerCase()),
  );
  return makeEntry?.image?.optimized || makeEntry?.image?.thumb || null;
};

const buildPriceTagConfig = (t: (k: string) => string) => ({
  GOOD_PRICE: { bg: '#F3F4F6', color: '#059669', icon: '↓', label: t('listing_details.good_price') },
  FAIR_PRICE: { bg: '#F3F4F6', color: '#6B7280', icon: '―', label: t('listing_details.fair_price') },
  HIGH_PRICE: { bg: '#F3F4F6', color: '#DC2626', icon: '↑', label: t('listing_details.high_price') },
});

export const ListingDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [shareCopied, setShareCopied] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  const {
    loading,
    listing,
    error,
    startingChat,
    isFavorited,
    similarListings,
    similarLoading,
    revealedPhone,
    revealingPhone,
    handleContactUser,
    handleRevealPhone,
    handleToggleFavorite,
    handleStartChat,
    handleShare,
  } = useListingDetails(id);

  const onShare = async () => {
    const copied = await handleShare();
    if (copied) {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    }
  };

  if (loading) return <ListingDetailSkeleton />;

  if (error || !listing) {
    return (
      <div className="home-page-light">
        <MainHeader />
        <div className="listing-details-container" style={{ textAlign: 'center', paddingTop: 120 }}>
          <div className="error-state">
            <CarOutlined className="error-state-icon" />
            <Text strong style={{ fontSize: 24, display: 'block', marginBottom: 8 }}>
              {t('listing_details.not_found')}
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              {t('listing_details.not_found_message')}
            </Text>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="btn-hp-primary"
              style={{ width: 'auto' }}
            >
              {t('listing_details.return_button')}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const statusCfg = getStatusConfig(listing.status, t);
  const brandLogo = getBrandLogo(listing.make);
  const tagCfg = listing.priceTag ? buildPriceTagConfig(t)[listing.priceTag] : null;
  const isElectric = listing.fuelType?.toLowerCase() === 'electric';
  const isImportedAndElectric = isElectric && !!listing.isImported;
  const themeColor = isImportedAndElectric ? '#1d4ed8' : isElectric ? '#16a34a' : '#fe5a1d';
  const displayLocation = listing.city || listing.location;

  const seoSlug = buildSlug(listing.make, listing.model, listing.year, listing.price, displayLocation ?? undefined);
  const seoTitle = buildListingTitle(listing.make, listing.model, listing.year, listing.price);
  const seoDesc = buildListingDescription(listing.make, listing.model, listing.year, listing.mileage, listing.fuelType ?? undefined, listing.price, displayLocation ?? undefined);
  const canonicalUrl = `${SITE_URL}/listing/${listing.id}/${seoSlug}`;
  const ogImage = listing.images?.[0]?.url ?? undefined;

  const vehicleSchema = buildVehicleJsonLd({
    id: listing.id,
    title: listing.title,
    make: listing.make,
    model: listing.model,
    year: listing.year,
    price: listing.price,
    mileage: listing.mileage,
    fuelType: listing.fuelType ?? undefined,
    transmission: listing.transmission ?? undefined,
    bodyType: listing.bodyType ?? undefined,
    color: listing.color ?? undefined,
    description: listing.description,
    images: listing.images?.map(img => img.url),
    city: displayLocation ?? undefined,
    slug: seoSlug,
  });

  const breadcrumbSchema = buildBreadcrumbJsonLd([
    { name: 'AutoPlace', url: SITE_URL },
    { name: 'Makina', url: `${SITE_URL}/mysearch` },
    { name: listing.make, url: `${SITE_URL}/mysearch?make=${encodeURIComponent(listing.make)}` },
    { name: `${listing.make} ${listing.model}`, url: `${SITE_URL}/mysearch?make=${encodeURIComponent(listing.make)}&model=${encodeURIComponent(listing.model)}` },
    { name: listing.title, url: canonicalUrl },
  ]);

  return (
    <div className={`home-page-light listing-details-page${isImportedAndElectric ? ' listing-details-page--imported-electric' : isElectric ? ' listing-details-page--electric' : ''}`}>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        canonical={canonicalUrl}
        ogImage={ogImage}
        ogType="product"
        jsonLd={[vehicleSchema, breadcrumbSchema]}
      />
      <MainHeader />

      <StickyHeader
        title={listing.title}
        price={listing.price}
        isNegotiable={listing.isNegotiable}
        isFavorited={isFavorited}
        startingChat={startingChat}
        onChat={isAuthenticated ? handleStartChat : () => message.warning(t('listing_details.login_to_chat'))}
        onCall={handleContactUser}
        onFavorite={handleToggleFavorite}
      />

      <main style={{ paddingTop: 80 }}>
        <div className="listing-details-container">

          {/* ── Breadcrumb ── */}
          <nav className="breadcrumb-nav">
            <button
              className="breadcrumb-current breadcrumb-link"
              onClick={() => navigate(`/mysearch?make=${encodeURIComponent(listing.make)}&model=${encodeURIComponent(listing.model)}`)}
            >
              {listing.make} {listing.model}
            </button>
          </nav>

          {/* ── Two-column layout: left = gallery + content, right = price card + seller ── */}
          <div className="listing-layout animate-hp delay-1" ref={heroRef}>

            {/* ── LEFT column ── */}
            <div className="layout-left">
              {/* Gallery */}
              <ImageGallery images={listing.images} title={listing.title} />

              {/* Overview */}
              <section className="content-section">
                <h2 className="section-title-hp">Overview</h2>
                <div className="overview-grid">
                  <div className="overview-item">
                    <span className="overview-label">Make</span>
                    <button
                      className="overview-value overview-filter-btn"
                      onClick={() => navigate(`/mysearch?make=${encodeURIComponent(listing.make)}`)}
                    >
                      {brandLogo
                        ? <img src={brandLogo} alt={listing.make} style={{ height: 28, objectFit: 'contain' }} />
                        : listing.make}
                    </button>
                  </div>
                  <div className="overview-item">
                    <span className="overview-label">Model</span>
                    <button
                      className="overview-value overview-filter-btn"
                      onClick={() => navigate(`/mysearch?make=${encodeURIComponent(listing.make)}&model=${encodeURIComponent(listing.model)}`)}
                    >
                      {listing.model}
                    </button>
                  </div>
                  <div className="overview-item">
                    <span className="overview-label">Year</span>
                    <span className="overview-value">{listing.year}</span>
                  </div>
                  {listing.condition && (
                    <div className="overview-item">
                      <span className="overview-label">Condition</span>
                      <span className="overview-value">{listing.condition}</span>
                    </div>
                  )}
                  <div className="overview-item">
                    <span className="overview-label">Location</span>
                    <span className="overview-value">{displayLocation || '—'}</span>
                  </div>
                  <div className="overview-item">
                    <span className="overview-label">Listed</span>
                    <span className="overview-value">{new Date(listing.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                </div>
                <ImportedVehicleSection
                  isImported={listing.isImported}
                  importCountry={listing.importCountry}
                  shippingIncluded={listing.shippingIncluded}
                  importAvailability={listing.importAvailability}
                  estimatedArrivalDate={listing.estimatedArrivalDate}
                />
              </section>

              {/* Specs */}
              <section className="content-section">
                <h2 className="section-title-hp">{t('listing_details.vehicle_details')}</h2>
                <SpecsGrid listing={listing} />
              </section>

              {/* Description */}
              {listing.description && (
                <section className="content-section">
                  <h2 className="section-title-hp">{t('listing_details.sellers_description')}</h2>
                  <Paragraph className="description-text">{listing.description}</Paragraph>
                </section>
              )}

              {/* Features */}
              {listing.features && listing.features.length > 0 && (
                <section className="content-section">
                  <h2 className="section-title-hp">
                    {t('listing_details.features_equipment')}
                    <span className="section-count">{listing.features.length}</span>
                  </h2>
                  <FeatureList features={listing.features} />
                </section>
              )}
            </div>

            {/* ── RIGHT column ── */}
            <div className="layout-right">
              {/* Price & Action Card */}
              <div className="listing-info-card">
                <div className="status-badge-container">
                  <Tag color={statusCfg.color} icon={statusCfg.icon} style={{ borderRadius: 6, padding: '2px 10px' }}>
                    {statusCfg.text}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ID: {listing.id.split('-')[0].toUpperCase()}
                  </Text>
                </div>

                <h1 className="listing-title">{listing.title}</h1>

                <Space size={8} wrap style={{ marginTop: 4 }}>
                  <button
                    className="filter-tag-btn"
                    onClick={() => navigate(`/mysearch?make=${encodeURIComponent(listing.make)}`)}
                    title={`Browse all ${listing.make}`}
                  >
                    {brandLogo
                      ? <img src={brandLogo} alt={listing.make} className="brand-logo-inline" />
                      : listing.make}
                  </button>
                  <button
                    className="filter-tag-btn"
                    onClick={() => navigate(`/mysearch?make=${encodeURIComponent(listing.make)}&model=${encodeURIComponent(listing.model)}`)}
                    title={`Browse all ${listing.make} ${listing.model}`}
                  >
                    {listing.model}
                  </button>
                  <span className="filter-tag-static">{listing.year}</span>
                </Space>

                <div className="price-block">
                  <div className="price-tag-large">
                    <span className="price-currency">€</span>
                    {listing.price.toLocaleString('de-DE')}
                  </div>
                  <div className="price-meta-row">
                    {listing.isNegotiable && <span className="price-negotiable">Negotiable</span>}
                    {listing.isImported && (
                      <span style={{
                        background: '#dbeafe',
                        color: '#1d4ed8',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '2px 10px',
                        borderRadius: 999,
                      }}>
                        Imported{listing.importCountry ? ` · ${listing.importCountry}` : ''}
                      </span>
                    )}
                    {tagCfg && (
                      <div className="price-tag-badge" style={{ background: tagCfg.bg, color: tagCfg.color }}>
                        <span>{tagCfg.icon}</span>
                        <span>{tagCfg.label}</span>
                      </div>
                    )}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                    {t('listing_details.final_price_note')}
                  </Text>
                </div>

                <div className="quick-specs-grid">
                  <div className="spec-item">
                    <div className="spec-icon"><MdSpeed /></div>
                    <div className="spec-details">
                      <span className="spec-label">Mileage</span>
                      <span className="spec-value">{listing.mileage.toLocaleString()} km</span>
                    </div>
                  </div>
                  <div className="spec-item">
                    <div className="spec-icon"><MdCalendarToday /></div>
                    <div className="spec-details">
                      <span className="spec-label">Year</span>
                      <span className="spec-value">{listing.year}</span>
                    </div>
                  </div>
                  {listing.fuelType && (
                    <div
                      className="spec-item"
                      style={isImportedAndElectric && isElectric ? { '--hp-primary': '#16a34a', '--hp-primary-rgb': '22,163,74', '--hp-bg-subtle': '#f0fdf4' } as React.CSSProperties : undefined}
                    >
                      <div className="spec-icon">
                        {listing.fuelType.toLowerCase() === 'electric' ? <MdEco /> : <MdLocalGasStation />}
                      </div>
                      <div className="spec-details">
                        <span className="spec-label">Fuel</span>
                        <span className="spec-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {listing.fuelType.replace(/_/g, ' ')}
                          {listing.fuelType.toLowerCase() === 'electric' && (
                            <MdEco style={{ color: '#16a34a', fontSize: 14 }} />
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                  {listing.transmission && (
                    <div className="spec-item">
                      <div className="spec-icon"><MdSettings /></div>
                      <div className="spec-details">
                        <span className="spec-label">Transmission</span>
                        <span className="spec-value">{listing.transmission}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="action-box">
                  <Button className="btn-hp-primary" icon={<MessageOutlined />} loading={startingChat} onClick={handleStartChat} block>
                    {t('listing_details.start_chat')}
                  </Button>
                  <Button
                    className="btn-hp-outline"
                    icon={<PhoneOutlined />}
                    onClick={handleContactUser}
                    block
                  >
                    Call Seller
                  </Button>
                  {listing.user?.whatsapp && (
                    <Button className="btn-hp-outline btn-whatsapp" icon={<WhatsAppOutlined />} href={`https://wa.me/${listing.user.whatsapp.replace(/\D/g, '')}`} target="_blank" block>
                      {t('listing_details.chat_whatsapp')}
                    </Button>
                  )}
                </div>

                <div className="action-secondary">
                  <Button type="text" icon={isFavorited ? <HeartFilled style={{ color: themeColor }} /> : <HeartOutlined />} onClick={handleToggleFavorite} className={`secondary-btn ${isFavorited ? 'favorited' : ''}`}>
                    {isFavorited ? 'Saved' : t('listing_details.save')}
                  </Button>
                  <Button type="text" icon={<ShareAltOutlined />} onClick={onShare} className={`secondary-btn ${shareCopied ? 'share-copied' : ''}`}>
                    {shareCopied ? t('listing_details.link_copied') : t('listing_details.share')}
                  </Button>
                  <Button
                    type="text"
                    icon={<FlagOutlined />}
                    className="secondary-btn report-btn"
                    onClick={() => isAuthenticated ? setReportOpen(true) : navigate('/login')}
                  >
                    Report
                  </Button>
                </div>
              </div>

              {/* Seller card */}
              <div className="seller-card-hp">
                <h2 className="section-title-hp" style={{ fontSize: 18, borderBottom: 'none' }}>
                  Seller Info
                </h2>
                <div className="seller-profile">
                  <div className="seller-avatar-hp">
                    {listing.user?.name?.charAt(0).toUpperCase() || <UserOutlined />}
                  </div>
                  <div className="seller-info-text">
                    <h4>{listing.user?.name || 'Seller'}</h4>
                    <p>{t('listing_details.member_since')} {new Date(listing.createdAt).getFullYear()}</p>
                  </div>
                </div>
                <Divider style={{ margin: '16px 0' }} />
                <div className="contact-list-hp">
                  {listing.user?.whatsapp && (
                    <a href={`https://wa.me/${listing.user.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="contact-item-hp contact-link contact-whatsapp">
                      <WhatsAppOutlined /><span>WhatsApp</span>
                    </a>
                  )}
                </div>
                {listing.dealer && (
                  <button
                    onClick={() => navigate(`/dealer/${listing.dealer!.id}`)}
                    style={{
                      display: 'block', width: '100%', marginTop: 14,
                      background: themeColor, color: '#fff', border: 'none',
                      borderRadius: 10, padding: '11px 0', fontSize: 14,
                      fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3,
                    }}
                  >
                    Visit Dealer Showroom
                  </button>
                )}
              </div>



              {/* Safety tips */}
              <div className="safety-tips-card">
                <div className="safety-tips-header">
                  <div className="safety-icon-wrap">
                    <InfoCircleOutlined style={{ color: themeColor, fontSize: 16 }} />
                  </div>
                  <Text strong style={{ fontSize: 14 }}>{t('listing_details.safety_tips_title')}</Text>
                </div>
                <div className="safety-tips-list">
                  {[
                    { icon: <MdLocationOn />, text: t('listing_details.safety_tip_1') },
                    { icon: <MdCreditCard />, text: t('listing_details.safety_tip_2') },
                    { icon: <MdWarning />,    text: t('listing_details.safety_tip_3') },
                  ].map((tip, i) => (
                    <div key={i} className="safety-tip-item">
                      <span className="safety-tip-emoji">{tip.icon}</span>
                      <Text style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)', lineHeight: '20px' }}>{tip.text}</Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>{/* end listing-layout */}

          {/* ── Similar Vehicles ── */}
          <SimilarVehicles listings={similarListings} loading={similarLoading} />
        </div>
      </main>

      <ReportModal
        open={reportOpen}
        listingId={listing.id}
        listingTitle={listing.title}
        onClose={() => setReportOpen(false)}
      />

      <Footer />
    </div>
  );
};

export default ListingDetails;
